import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import * as React from "react";
import FalconHeader from "../../../components/navigation/FalconHeader";
import {UniqueSellingPointWidget} from "@ferry/plugin-ferry-journey/dist/widgets/annual/motor/UniqueSellingPointWidget";
import {Policy, ProductsEnum} from "@ferry/journey-services";
import {Footer, resourcesHelper} from "@ferry/portal-core";
import {BackButton} from "../../../components/navigation/BackButton";
import {deleteSessionStorage} from "../../../CommonFunctions";
import {PluginTemplates} from "../../../utils/PluginTemplates";
import {lazyInject} from "@athena-portal/core";
import {Types} from "../../../Types";
import type {ICustomerPortalService} from "../../../services/ICustomerPortalService";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IState {
    product?: string,
    brandName: string,
    isLoading: boolean,
}

@authorize
export class HelpPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: Readonly<IWithRouterProps<IParams>>) {
        super(props);
        this.state = {
            isLoading: true,
            product: "",
            brandName: resourcesHelper.THEME_NAME,
        }
    }

    public async componentDidMount() {
        window.scrollTo(0, 0);
        this.pluginTemplates.setPageTitle("Help");

        const policyNumber: string = this.props.params.policyNumber;

        this.customerPortalService.getPolicy(policyNumber).subscribe((policy: Policy) => {
            const product = policy.products[0];
            this.setState({
                product,
                brandName: policy.interaction?.brandName ?? resourcesHelper.THEME_NAME,
                isLoading: false
            })
        });
    }

    public componentWillUnmount() {
        window.removeEventListener("beforeunload", this.clearSession);
        this.clearSession();
    }

    public render(): React.ReactNode {
        return (
            <>
                <Overlay show={this.state.isLoading} />
                {!this.state.isLoading && this.renderContent()}
            </>
        )
    }

    private openLiveChat = () => {
        if (String(process.env.REACT_APP_CONTENT_GURU_CHAT_DISABLED).toLowerCase() === "true") {
            if (typeof window.WebChat === 'function') {
                window.WebChat("command", "Messenger.open", () => {});
            } else {
                console.warn("Genesys is not loaded yet");
            }
        } else {
            const webchatToggle: HTMLElement | null = document.getElementById("webchat-toggle-button");

            if (webchatToggle) {
                webchatToggle.click(); // a bit of a hack, but doesn't look like the new webchat exposes any API for us to use
            }
        }
    };

    public renderContent = () => {
        const policyNumber: string = this.props.params.policyNumber;
        const isDriveAwayPolicy = ProductsEnum.DRIVE_AWAY === this.state.product;
        const backButtonLink = `/dashboard/${isDriveAwayPolicy ? "driveAway" : "annualMotor"}/${policyNumber}`;

        return (
            <>
                <FalconHeader/>
                <div className="help-page">
                    <BackButton isTop={true} link={backButtonLink} />
                    <h1 className="help-title">Help</h1>
                    <p className="description-text">We like to make things easy and save everyone time. Our online team are here for you if you have a question or need some help.</p>
                    <h2 className="communication-title">Ways to get in touch</h2>
                    <ul>
                        <li className="underline"><a href={`mailto:${resourcesHelper.THEME.toLowerCase()}carinsurance@allianz.co.uk`}>Email us</a></li>
                        <li className="underline">
                            <button
                                onClick={this.openLiveChat}
                                className="unstyled-button"
                            >
                                Live chat
                            </button>
                        </li>
                        <li>If you prefer to speak to someone, please call us on: <span className="phone-number">{resourcesHelper.CONTACT_PHONE_NUMBER}</span></li>
                        <li className="no-bullet">Mon to Fri 8am - 8pm</li>
                        <li className="no-bullet">Sat and bank holidays 9am - 5pm</li>
                        <li className="no-bullet">Sun Closed</li>
                        <li className="no-bullet">For Text Phone first dial 18001</li>
                        <li className="no-bullet">Calls will be recorded</li>
                    </ul>
                    <UniqueSellingPointWidget brandName={this.state.brandName}/>
                    <p className="faq-description">We’ve answered your questions before you even thought of them! Head to our <span className="underline">
                            <a href={resourcesHelper.FAQ_URL} target="_blank">FAQs</a>
                        </span> section to save yourself time
                    </p>
                    <p className="faq-title">Most common questions</p>
                    <h3 className="faq-question">How do I manage my policy?</h3>
                    <div className="faq-answer">
                        <p>Simply sign into your account to manage your policy. Here's what you can do in your online account...</p>
                        <ul>
                            <li>add a new driver to your cover</li>
                            <li>change your car</li>
                            <li>add optional extras</li>
                            <li>cancel or renew your cover</li>
                            <li>manage your payments</li>
                            <li>see your policy documents</li>
                        </ul>
                    </div>
                    <h3 className="faq-question">Where can I find my policy documents?</h3>
                    <div className="faq-answer">
                        <p>You can access your policy documents <a href={`/dashboard/${isDriveAwayPolicy ?
                                "driveAwayDocuments" : "annualMotorDocuments"}/${policyNumber}`}>here</a>
                        </p>
                    </div>
                    <BackButton isTop={true} link={backButtonLink} />
                </div>
                <Footer/>
            </>
        );
    }

    private clearSession = (): void => {
        deleteSessionStorage("onlineMotorClaimRequest");
    };
}

export const HelpPage = withRouter(HelpPagePlain)

